import { useEffect, useRef } from 'react';
import { Outlet, useLocation, useNavigationType } from 'react-router-dom';

const NAVBAR_OFFSET_PX = 90;
const MAX_HASH_SCROLL_ATTEMPTS = 20;
const HASH_SCROLL_RETRY_DELAY_MS = 100;
const SCROLL_POSITIONS_STORAGE_KEY = 'lynorg:scroll-positions:v1';

const scrollToY = (top, behavior = 'auto') => {
	window.scrollTo({ top, left: 0, behavior });
	document.documentElement.scrollTop = top;
	document.body.scrollTop = top;
};

const readScrollPositions = () => {
	try {
		const raw = sessionStorage.getItem(SCROLL_POSITIONS_STORAGE_KEY);
		if (!raw) return {};
		const parsed = JSON.parse(raw);
		return parsed && typeof parsed === 'object' ? parsed : {};
	} catch {
		return {};
	}
};

const writeScrollPositions = (positions) => {
	try {
		sessionStorage.setItem(SCROLL_POSITIONS_STORAGE_KEY, JSON.stringify(positions));
	} catch {
		// ignore write failures (private mode, quota, etc.)
	}
};

const saveScrollPosition = (locationKey, y) => {
	if (!locationKey) return;
	const positions = readScrollPositions();
	positions[locationKey] = typeof y === 'number' ? y : 0;
	writeScrollPositions(positions);
};

const getSavedScrollPosition = (locationKey) => {
	if (!locationKey) return null;
	const positions = readScrollPositions();
	const value = positions[locationKey];
	return typeof value === 'number' ? value : null;
};

const scrollToHashTarget = (hash) => {
	const targetId = decodeURIComponent(hash.replace('#', '').trim());
	if (!targetId) return false;

	const targetElement = document.getElementById(targetId);
	if (!targetElement) return false;

	targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });

	setTimeout(() => {
		const y = targetElement.getBoundingClientRect().top + window.pageYOffset - NAVBAR_OFFSET_PX;
		scrollToY(y, 'smooth');
	}, 40);

	return true;
};

const NavigationScroll = () => {
	const { pathname, hash, key } = useLocation();
	const navigationType = useNavigationType();
	const previousKeyRef = useRef(key);

	useEffect(() => {
		const previousKey = previousKeyRef.current;
		if (previousKey && previousKey !== key) {
			saveScrollPosition(previousKey, window.scrollY);
		}
		previousKeyRef.current = key;

		if (!hash) {
			if (navigationType === 'POP') {
				const savedY = getSavedScrollPosition(key);
				if (typeof savedY === 'number') {
					scrollToY(savedY, 'auto');
					setTimeout(() => scrollToY(savedY, 'auto'), 50);
					return;
				}
			}

			scrollToY(0, 'auto');
			return;
		}

		let attempt = 0;

		const tryScroll = () => {
			if (scrollToHashTarget(hash)) return;

			attempt += 1;
			if (attempt < MAX_HASH_SCROLL_ATTEMPTS) {
				setTimeout(tryScroll, HASH_SCROLL_RETRY_DELAY_MS);
				return;
			}

			scrollToY(0);
		};

		tryScroll();
	}, [pathname, hash, key, navigationType]);

	return <Outlet />;
};

export default NavigationScroll;

